import { RedoSideNavigation } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { filterTruthy } from "@redotech/util/array";
import { memo, useMemo } from "react";
import { useAeoNavigation } from "./aeo/aeo-navigation";
import { useConversionNavigation } from "./conversion/conversion-navigation";
import { useCustomersNavigation } from "./customers/customers-navigation";
import { useDeveloperNavigation } from "./developer/developer-navigation";
import { useHomeNavigation } from "./home/home-navigation";
import { useInventoryNavigation } from "./inventory/inventory-navigation";
import { useMarketingNavigation } from "./marketing/marketing-navigation";
import { useOrdersPrimaryNavigation } from "./orders/orders-primary-navigation";
import { usePaymentsNavigation } from "./payments/payments-navigation";
import { useRedoSuppliesNavigation } from "./redo-supplies/redo-supplies-navigation";
import { useReturnsNavigation } from "./returns/returns-navigation";
import { useSettingsItems } from "./settings";
import { useShippingNavigation } from "./shipping/shipping-navigation";
import { useShopperAgentNavigation } from "./shopper-agent/shopper-agent-navigation";
import { useSupportNavigation } from "./support/support-navigation";

export const MerchantAppSideNav = memo(function MerchantAppSideNav({
  enterSettingsUrl,
  exitSettingsUrl,
  settingsRoot,
  hideNav,
  hideTeamSwitcher = false,
}: {
  enterSettingsUrl: string;
  exitSettingsUrl: string;
  settingsRoot: string;
  hideNav: boolean;
  hideTeamSwitcher?: boolean;
}) {
  const homeEntry = useHomeNavigation();
  const aeoEntry = useAeoNavigation();
  const conversionEntry = useConversionNavigation();
  const marketing = useMarketingNavigation();
  const shopperAgent = useShopperAgentNavigation();
  const support = useSupportNavigation();
  const customers = useCustomersNavigation();
  const payments = usePaymentsNavigation();
  const redoSupplies = useRedoSuppliesNavigation();
  const developer = useDeveloperNavigation();

  // Separate Shipping and Returns sections + standalone Orders tab
  const orders = useOrdersPrimaryNavigation();
  const shipping = useShippingNavigation();
  const returns = useReturnsNavigation();

  const inventory = useInventoryNavigation();

  const allPageEntries = useMemo(() => {
    // `orders` must precede `shipping`: its pathRoot is a strict prefix of
    // shipping's, so selectedPrimaryTab find() would else highlight Shipping.
    return filterTruthy([
      homeEntry,
      aeoEntry,
      conversionEntry,
      marketing,
      shopperAgent,
      orders,
      shipping,
      returns,
      inventory,
      support,
      customers,
      payments,
      redoSupplies,
      developer,
    ]);
  }, [
    homeEntry,
    aeoEntry,
    conversionEntry,
    marketing,
    shopperAgent,
    orders,
    shipping,
    returns,
    inventory,
    support,
    customers,
    payments,
    redoSupplies,
    developer,
  ]);

  const settingsItems = useSettingsItems();

  return (
    <RedoSideNavigation
      enterSettingsUrl={enterSettingsUrl}
      exitSettingsUrl={exitSettingsUrl}
      hideTeamSwitcher={hideTeamSwitcher}
      isHidden={hideNav}
      pageEntries={allPageEntries}
      settingSections={settingsItems}
      settingsRoot={settingsRoot}
    />
  );
});
